import { logClientError } from '../services/opsLog';
// 로케일 의존 표시 포맷 — 단일 진입점.
//
// [왜 한곳으로 모으나]
// 예전에는 toLocaleString('ko-KR') 같은 호출이 17곳에 흩어져 있었다. 언어를 바꿔도
// 숫자·날짜는 한국식으로 남았고, 새 화면을 만들 때마다 로케일이 또 하드코딩됐다.
// 여기서 현재 i18n 언어를 읽어 Intl 에 넘기므로, 언어를 바꾸면 전부 따라온다.
//
// [날짜는 브라우저 타임존으로 표시한다]
// 백엔드는 값을 ISO 문자열로 직렬화한다(queryExecutor 의 convertRowValues).
// 즉 변환 지점이 여기 한 곳뿐이라, 보는 사람의 타임존으로 맞추는 것이 자연스럽다.
// (서버의 process.env.TZ='Asia/Seoul' 은 로그·수집 타임스탬프용이라 건드리지 않는다)
import i18n from '../i18n';

/** 현재 언어에 대응하는 BCP 47 로케일. Intl 은 'ko' 만으로도 동작하지만 지역까지 주면 더 정확하다. */
function currentLocale() {
    const lang = (i18n && i18n.language) || 'ko';
    return lang === 'ko' ? 'ko-KR' : 'en-US';
}

/** 숫자에 천 단위 구분 기호. null/빈값은 dash 로. */
export function formatNumber(value, { dash = '-', ...opts } = {}) {
    if (value == null || value === '') return dash;
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return new Intl.NumberFormat(currentLocale(), opts).format(n);
}

/** 초 단위 소요시간. 기본 소수 2자리. */
export function formatSeconds(value, { dash = '-', digits = 2 } = {}) {
    if (value == null || value === '') return dash;
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return `${formatNumber(n, { minimumFractionDigits: digits, maximumFractionDigits: digits })}s`;
}

/**
 * 바이트를 사람이 읽는 단위로. 단위 기호는 로케일 무관(SI 표기)이지만
 * 숫자 부분은 로케일 구분 기호를 따른다.
 */
export function formatBytes(value, { dash = '-' } = {}) {
    if (value == null || value === '') return dash;
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    if (n < 1024) return `${formatNumber(n)}B`;
    if (n < 1024 * 1024) return `${formatNumber(n / 1024, { maximumFractionDigits: 1 })}KB`;
    if (n < 1024 * 1024 * 1024) return `${formatNumber(n / 1024 / 1024, { maximumFractionDigits: 1 })}MB`;
    return `${formatNumber(n / 1024 / 1024 / 1024, { maximumFractionDigits: 2 })}GB`;
}

/** 날짜+시각. ISO 문자열/Date/epoch 를 받는다. 파싱 실패 시 원본을 그대로 돌려준다. */
export function formatDateTime(value, { dash = '-', ...opts } = {}) {
    if (value == null || value === '') return dash;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return new Intl.DateTimeFormat(currentLocale(), {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        ...opts,
    }).format(d);
}

/** 시각만 (실시간 모니터링 축 등). */
export function formatTime(value, { dash = '-' } = {}) {
    return formatDateTime(value, {
        dash, year: undefined, month: undefined, day: undefined,
    });
}

/** 날짜만. */
export function formatDate(value, { dash = '-' } = {}) {
    return formatDateTime(value, {
        dash, hour: undefined, minute: undefined, second: undefined,
    });
}

/**
 * 백엔드 오류를 사용자 문구로.
 * errorType 코드가 있으면 번역하고, 없으면 서버가 준 메시지를 그대로 쓴다.
 * (백엔드 문자열 560개를 번역 대상에 넣지 않기 위한 장치 — 회귀가 없다)
 */
/**
 * 화면 코드에서 난 예외를 기록한다 (화면 오류 수집 2단계, 2026-09-14). catch 로 잡아 토스트만 띄우는 곳은
 * 전역 수집기(처리되지 않은 오류만 본다)가 못 보는데, 그런 자리는 거의 다 여기서 문구를 만든다.
 * 서버·네트워크 요청 실패는 서버가 이미 남기므로 넘긴다(두 번 세어진다). 같은 오류 객체는 한 번만.
 */
const notedClientErrors = new WeakSet();
function noteClientError(err) {
    try {
        if (!(err instanceof Error) || notedClientErrors.has(err)) return;
        if (err.isAxiosError || err.response || err.request || err.body || err.errorType) return;
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        if (err.name === 'QbaseError') return;   // QBase 서버가 돌려준 실패 — 화면 코드의 예외가 아니다
        notedClientErrors.add(err);
        logClientError('action', err);
    } catch (_e) { /* 기록이 문구를 막으면 안 된다 */ }
}

export function formatApiError(err, t) {
    noteClientError(err);
    // 어시스턴트 API(services/assistantClient)는 fetch 라 response 가 없고 본문을 err.body 에 담는다 — 같은 자리로 읽는다.
    // 안 그러면 서버가 사유(NO_CREDENTIAL 등)를 줘도 아래에서 「백엔드에 연결할 수 없다」 로 바뀐다(2026-10-06 설계 가이드 변환에서 겪음)
    const data = (err && err.response && err.response.data) || (err && err.body && typeof err.body === 'object' ? err.body : null);
    const code = data && data.errorType;
    if (code && t) {
        const key = `common:errors.${code}`;
        // 백엔드가 data.params 를 같이 주면 보간에 넘긴다 (참조 중인 이름 목록 등).
        // 키가 없으면 t() 는 키를 돌려주는데, i18next 는 네임스페이스 접두사를 뗀
        // 'errors.CODE' 형태로 에코한다 — 접두사 포함 비교만 하면 미번역을 못 잡아
        // 화면에 날키가 그대로 나간다 (실제로 SERVER_ERROR 에서 겪음).
        const translated = t(key, (data && data.params) || {});
        const echoed = key.slice(key.indexOf(':') + 1);
        if (translated !== key && translated !== echoed) return translated;   // 번역이 있으면 그것을
    }
    // 번역 키가 없으면 서버 메시지 그대로. 백엔드는 사용자용 문구를 message 에,
    // 원인(ORA-xxxxx 등)을 error 에 담는 경우가 있어 message 를 먼저 본다.
    if (data && data.message) return data.message;
    if (data && data.error) return data.error;
    // QBase 직접 호출(services/qbaseClient)은 axios 가 아니라 fetch 라 response 가 없다 — 서버가 준 문구(resultMessage 등)를
    // 담은 QbaseError 다. 이것을 「백엔드에 연결할 수 없다」 로 바꾸면 실제 원인이 가려진다(2026-10-06 클라우드 저장 500 에서 겪음)
    if (err && err.name === 'QbaseError' && err.message) return err.message;
    // 응답을 받았다(상태 코드가 있다) — 연결 실패가 아니다
    if (err && Number(err.status) > 0 && err.message) return err.message;
    if (!err || !err.response) {
        return t ? t('common:errors.backendUnreachable') : '백엔드 서버에 연결할 수 없습니다.';
    }
    return (err && err.message) || (t ? t('common:errors.unknown') : '알 수 없는 오류가 발생했습니다.');
}
